import { useEffect, useMemo } from 'react';
import { useGetDashboardOverviewQuery } from '../../../../utils/api_service/dashboardApi';
import { toast } from '../../../../ui/toast';
import { ADMIN_JOB_OPENING_LABEL_ORDER, ADMIN_SESSION_LABELS } from '../constants';
import type { DashboardData } from '../../../../utils/types/DashboardTypes';

/** Overview metrics only. Tab + search routing lives in useAdminDashboardTabs. */
export const useAdminDashboard = () => {
  const { data: timeData, isLoading, isFetching, error } = useGetDashboardOverviewQuery();

  useEffect(() => {
    if (error) {
      toast.error('Error Fetching Analytics', { position: 'bottom-center' });
    }
  }, [error]);

  const orderedJobOpenings = useMemo(() => {
    const jobOpenings: DashboardData['jobOpenings'] | undefined = timeData?.jobOpenings;
    if (!jobOpenings) return undefined;
    return Object.fromEntries(
      Object.entries(jobOpenings).map(([period, dataArray]) => [
        period,
        ADMIN_JOB_OPENING_LABEL_ORDER.map((label) => {
          const found = dataArray.find((d) => d.label === label);
          return found ?? { label, value: 0 };
        }),
      ]),
    ) as DashboardData['jobOpenings'];
  }, [timeData]);

  const mappedUserAssessments = useMemo(() => {
    const userAssessments: DashboardData['userAssessments'] | undefined = timeData?.userAssessments;
    if (!userAssessments) return undefined;
    return Object.fromEntries(
      Object.entries(userAssessments).map(([period, dataArray]) => [
        period,
        ADMIN_SESSION_LABELS.map((label) => {
          const found = dataArray.find(
            (d) =>
              d.label === label ||
              d.label.toLowerCase() === label.toLowerCase() ||
              (label === 'Completed' && d.label.toLowerCase() === 'complete'),
          );
          return { label, value: found ? found.value : 0 };
        }),
      ]),
    ) as DashboardData['userAssessments'];
  }, [timeData]);

  return {
    loading: isLoading || isFetching,
    timeData,
    orderedJobOpenings,
    mappedUserAssessments,
  };
};
